/**
 * ViewType registry (spec §9.7, §9.10, I-9): built-in widgets register here exactly like plugins.
 * Every registration declares its level (spec §9.24); default is `power`.
 */
import type { ComponentType } from "react";
import type { LoroDoc } from "loro-crdt";
import type { DocHandle } from "../sync/docManager";
import type { Level } from "../core/schema";

export interface ViewCtx {
  doc: LoroDoc;
  handle: DocHandle;
  workspaceId: string;
  /** 1 Hz-throttled clock (ms) so computed props refresh (spec §9.7) */
  now: number;
  level: Level;
  readOnly: boolean;
  /** `kid` profiles get no tracking/pomodoro UI (spec §9.29) */
  profileKind: "person" | "kid" | "viewer";
  openTask(id: string): void;
  write(fn: (doc: LoroDoc) => void): void;
}

export interface ViewProps<Cfg> {
  config: Cfg;
  ctx: ViewCtx;
  title?: string;
  /** dashboard edit mode: the widget may show its own config affordances */
  editing?: boolean;
  /** the widget's id on its dashboard, for per-device view state (zoom, positions) */
  widgetId?: string;
  /** shown for another workspace under "Show from" (ADR 0024): nothing at all when there is nothing to show */
  quietEmpty?: boolean;
}

export interface ViewType<Cfg = Record<string, unknown>> {
  id: string;
  /** i18n key for the label */
  label: string;
  icon: string;
  level: Level;
  defaultConfig: Cfg;
  /** JSON Schema (subset, see dashboards/ConfigForm.tsx) for the config form */
  configSchema?: import("../dashboards/ConfigForm").JsonSchema;
  Component: ComponentType<ViewProps<Cfg>>;
  /** minimum grid size, in dashboard units */
  minSize?: { w: number; h: number };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyViewType = ViewType<any>;

const registry = new Map<string, AnyViewType>();
const listeners = new Set<() => void>();

let version = 0;

export function registerViewType<Cfg>(vt: ViewType<Cfg>): void {
  registry.set(vt.id, vt as AnyViewType);
  version++;
  for (const l of listeners) l();
}

/** Changes whenever a view type is registered (plugins arrive after boot); for `useSyncExternalStore`. */
export function viewTypesVersion(): number {
  return version;
}

export function getViewType(id: string): AnyViewType | undefined {
  return registry.get(id);
}

export function listViewTypes(level?: Level): AnyViewType[] {
  const order: Level[] = ["simple", "standard", "power"];
  const max = level ? order.indexOf(level) : 2;
  return [...registry.values()].filter((v) => order.indexOf(v.level) <= max);
}

export function subscribeViewTypes(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
